import React, { useEffect, useState } from "react";
import templateService from "../../../services/template.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import Swal from 'sweetalert2';

const InstallTemplateDashboard = () => {
    const [ecxTemplates, setEcxTemplates] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState("");
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [installing, setInstalling] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch both templates and clinics in parallel
                const [templatesResponse, clinicsResponse] = await Promise.all([
                    templateService.getEcxTemplate(0, 1000),
                    clinicService.getAllClinicsFast() // Adjust this based on your clinic service
                ]);

                // Filter templates to only show usedBy: CLINIC
                const clinicTemplates = templatesResponse.data.content.filter(
                    template => template.usedBy === "CLINIC"
                );

                setEcxTemplates(clinicTemplates);
                setClinics(clinicsResponse.data); // Adjust based on your API response structure
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load data. Please try again.',
                    confirmButtonColor: '#dc3545'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedTemplates(ecxTemplates.map(template => template.id));
        } else {
            setSelectedTemplates([]);
        }
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplates(prev => {
            if (prev.includes(templateId)) {
                return prev.filter(id => id !== templateId);
            } else {
                return [...prev, templateId];
            }
        });
    };

    const handleInstallTemplates = async () => {
        if (!selectedClinic) {
            Swal.fire({
                icon: 'warning',
                title: 'Clinic Required',
                text: 'Please select a clinic first.',
                confirmButtonColor: '#0d6efd'
            });
            return;
        }

        if (selectedTemplates.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Templates Required',
                text: 'Please select at least one template to install.',
                confirmButtonColor: '#0d6efd'
            });
            return;
        }

        try {
            setInstalling(true);

            // Prepare installation data
            const installationData = {
                clinicId: selectedClinic,
                templatesList: selectedTemplates
            };

            // Call install API
            const response = await templateService.installTemplates(installationData);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: `Successfully installed ${selectedTemplates.length} template(s) to the clinic.`,
                confirmButtonColor: '#198754',
                timer: 3000,
                showConfirmButton: true
            });

            // Reset selections
            setSelectedTemplates([]);
            setSelectedClinic("");

        } catch (error) {
            console.error('Error installing templates:', error);
            Swal.fire({
                icon: 'error',
                title: 'Installation Failed',
                text: error.response?.data?.message || 'Failed to install templates. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setInstalling(false);
        }
    };

    const getMediumTypeBadge = (mediumType) => {
        const badgeConfig = {
            'EMAIL': { class: 'service-status status-completed text-success', text: 'Email' },
            'SMS': { class: 'service-status status-pending text-info', text: 'SMS' },
            'CALL': { class: 'service-status status-incomplete text-warning', text: 'Call' }
        };

        const config = badgeConfig[mediumType] || { class: 'bg-secondary', text: mediumType };
        return <span className={`badge ${config.class} me-1`}>{config.text}</span>;
    };

    if (loading) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-cloud-arrow-down me-2"></i> Install Templates</h1>
                </div>
                <Loading />
            </div>
        );
    }

    return (
        <div className="container container-xxl">
            <div className="pagetitle">
                <h1><i className="bi bi-cloud-arrow-down me-2"></i> Install Templates</h1>
            </div>

            {/* Clinic Selection Card */}
            <div className="card card-padding mb-4">
                <div className="card-title mb-0">
                    <i className="bi bi-building me-2"></i>
                    Select Clinic
                </div>
                <div className="card-body">
                    <div className="row d-flex justify-content-between align-items-center">
                        <div className="col-8">
                            <label className="form-label fw-semibold">Choose a Clinic *</label>
                            <select
                                className="form-control form-select"
                                value={selectedClinic}
                                onChange={(e) => setSelectedClinic(e.target.value)}
                                required
                            >
                                <option value="">Select a clinic</option>
                                {clinics.map(clinic => (
                                    <option key={clinic.id} value={clinic.id}>
                                        {clinic.name} {clinic.city ? `- ${clinic.city}` : ''}
                                    </option>
                                ))}
                            </select>
                            <div className="form-text">
                                Select the clinic where you want to install the templates
                            </div>
                        </div>
                        <div className=" col-4 d-flex align-items-end mt-1">
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleInstallTemplates}
                                disabled={!selectedClinic || selectedTemplates.length === 0 || installing}
                            >
                                {installing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Installing...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cloud-arrow-down me-2"></i>
                                        Install Selected Templates ({selectedTemplates.length})
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Templates List Card */}
            <div className="card">
                <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                            <i className="bi bi-file-earmark-medical me-2"></i>
                            Available Templates (Used by Clinic)
                        </h5>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="selectAll"
                                checked={selectedTemplates.length === ecxTemplates.length && ecxTemplates.length > 0}
                                onChange={handleSelectAll}
                                disabled={ecxTemplates.length === 0}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="selectAll">
                                Select All
                            </label>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {ecxTemplates.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-inbox display-4 text-muted"></i>
                            <h5 className="text-muted mt-3">No Clinic Templates Available</h5>
                            <p className="text-muted">There are no templates with usedBy: CLINIC.</p>
                        </div>
                    ) : (

                        <div className="table-responsive mt-2">
                            <table className="table table-hover table-bordered">
                                <thead>
                                <tr>
                                    <th width="50px" className="text-center">Select</th>
                                    <th>Template Name</th>
                                    <th>Type</th>
                                    <th>Content Preview</th>
                                    <th>Created</th>
                                </tr>
                                </thead>
                                <tbody>
                                {ecxTemplates.map(template => (
                                    <tr key={template.id} className={selectedTemplates.includes(template.id) ? 'table-primary' : ''}>
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedTemplates.includes(template.id)}
                                                onChange={() => handleTemplateSelect(template.id)}
                                            />
                                        </td>
                                        <td>
                                            <div className="fw-semibold">{template.reminderName}</div>
                                            <small className="text-muted">ID: {template.id}...</small>
                                        </td>
                                        <td>
                                            {getMediumTypeBadge(template.mediumType)}
                                        </td>
                                        <td>
                                            <div className="text-truncate" style={{maxWidth: '300px'}} title={template.content}>
                                                {template.content}
                                            </div>
                                        </td>
                                        <td>
                                            <small className="text-muted">{template.createdAt}</small>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="card-footer bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">
                            Showing {ecxTemplates.length} clinic templates
                        </span>
                        <span className="badge bg-info">
                            {selectedTemplates.length} template(s) selected
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstallTemplateDashboard;